import Typography from "@mui/material/Typography";

import { useSlackOutboundChannels } from "@/shared/domain/slack/hooks";

interface SlackChannelLinkProps {
  channelId: string;
}

/**
 * Resolves a Slack channel id to its `#name`, linked into Slack.
 *
 * The org's outbound allowlist is the only place Roboto knows a channel's name,
 * so a channel an admin has since revoked resolves to nothing — it renders the
 * raw id rather than a broken link, because a dispatch that posted there is
 * still real history.
 *
 * The link goes to the channel, not the message. A message permalink needs the
 * workspace's own subdomain, which Roboto does not store; `app.slack.com` works
 * from the workspace id alone, which the allowlist row carries.
 */
export function SlackChannelLink({ channelId }: SlackChannelLinkProps) {
  const channelsQuery = useSlackOutboundChannels();
  const channel = channelsQuery.data?.find(
    (candidate) => candidate.channel_id === channelId,
  );

  if (!channel) {
    return (
      <Typography variant="body2" component="span" title={channelId}>
        {channelId}
      </Typography>
    );
  }

  return (
    <a
      href={`https://app.slack.com/client/${encodeURIComponent(
        channel.team_id,
      )}/${encodeURIComponent(channel.channel_id)}`}
      target="_blank"
      rel="noreferrer"
      title={`Open #${channel.channel_name} in Slack`}
    >
      #{channel.channel_name}
    </a>
  );
}
